/**
 * LandingWidgets — Role-based landing page with live ERP widgets.
 *
 * Renders a personalized greeting + a responsive grid of data widgets
 * based on the user's RBAC role (widget list comes from /api/auth/me).
 *
 * Each widget:
 *  - Fetches data from a backend tool on mount
 *  - Shows a skeleton loader while fetching
 *  - Displays a summary badge/count on success
 *  - Has an "Ask Trendy" quick-action that pre-fills the chat input
 *  - Error state shows clear retry action with visual distinction
 *  - Staggered entrance animation for polished feel
 */

import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AlertTriangle,
  ArrowRight,
  BadgeDollarSign,
  BarChart3,
  Brain,
  Building2,
  Calendar,
  CheckCircle2,
  Clock,
  CreditCard,
  DollarSign,
  FileText,
  Lightbulb,
  PackageCheck,
  RefreshCw,
  Search,
  ShieldAlert,
  ShoppingCart,
  Truck,
  TrendingUp,
  Wrench,
  XCircle,
} from 'lucide-react'
import { executeTool } from '../../lib/api'


// =============================================================================
// Widget Registry — maps widget ID → config (icon, tool call, display logic)
// =============================================================================

const WIDGET_REGISTRY = {
  cash_position: {
    title: 'Cash Position',
    icon: DollarSign,
    color: 'emerald',
    toolName: 'daily_cash_position',
    toolParams: { include_bank: false },  // Skip Plaid on landing page ($0.10/call)
    extract: (data) => {
      const r = data?.result || data
      // Without bank balance, show AR-based net position
      const net = r?.net_position_7d ?? r?.net_position ?? r?.current_position?.net_position
      if (net != null) return { value: fmtCurrency(net), label: 'AR/AP net 7-day' }
      const arTotal = r?.ar?.total
      if (arTotal != null) return { value: fmtCurrency(arTotal), label: 'Total AR' }
      return { value: '--', label: 'Net position' }
    },
    question: "What's our full cash position today including bank balances?",
  },
  ar_alerts: {
    title: 'AR Alerts',
    icon: AlertTriangle,
    color: 'red',
    toolName: 'check_ar_alerts',
    toolParams: {},
    extract: (data) => {
      const r = data?.result || data
      const total = r?.count ?? r?.total_alerts ?? r?.alerts?.length ?? 0
      const pastDue = r?.total_past_due ?? 0
      return {
        value: String(total),
        label: pastDue > 0 ? `$${(pastDue/1000).toFixed(0)}K past due` : 'Past due invoices',
        alert: total > 0,
      }
    },
    question: 'Show me all AR alerts — any overdue invoices or large balances?',
  },
  ap_alerts: {
    title: 'AP Alerts',
    icon: CreditCard,
    color: 'orange',
    toolName: 'check_ap_alerts',
    toolParams: {},
    extract: (data) => {
      const r = data?.result || data
      const overdueCount = r?.overdue?.count ?? 0
      const discountCount = r?.discounts_expiring?.count ?? 0
      const total = overdueCount + discountCount
      return {
        value: String(total),
        label: overdueCount > 0 ? `${overdueCount} overdue` : (discountCount > 0 ? `${discountCount} discounts expiring` : 'No alerts'),
        alert: overdueCount > 0,
      }
    },
    question: 'Show me AP alerts — any past-due vendor invoices?',
  },
  late_jobs: {
    title: 'Late Jobs',
    icon: Clock,
    color: 'red',
    toolName: 'check_late_jobs',
    toolParams: {},
    extract: (data) => {
      const r = data?.result || data
      const count = r?.count ?? r?.total_late ?? r?.jobs?.length ?? 0
      return {
        value: String(count),
        label: 'Jobs past due',
        alert: count > 0,
      }
    },
    question: 'Which jobs are past their due date? Show me the most critical ones.',
  },
  job_overruns: {
    title: 'Job Overruns',
    icon: TrendingUp,
    color: 'amber',
    toolName: 'check_job_overruns',
    toolParams: {},
    extract: (data) => {
      const r = data?.result || data
      const count = r?.count ?? r?.overruns?.length ?? 0
      const impact = r?.total_cost_impact ?? 0
      return {
        value: String(count),
        label: impact > 0 ? `$${(impact/1000).toFixed(0)}K impact` : 'Over budget',
        alert: count > 0,
      }
    },
    question: 'Show me jobs running over budget — what are the biggest cost overruns?',
  },
  expiring_quotes: {
    title: 'Quote Follow-Ups',
    icon: FileText,
    color: 'amber',
    toolName: 'search_quotes',
    toolParams: {
      status: 'Open',
      follow_up_from: new Date().toISOString().slice(0, 10),
      follow_up_to: new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10),
      limit: 2000,
      count_only: true,
    },
    extract: (data) => {
      const r = data?.result || data
      const count = r?.total ?? r?.count ?? 0
      return {
        value: String(count),
        label: 'Follow-ups next 7 days',
        alert: count > 0,
      }
    },
    question: 'Which quotes need follow-up this week? Show me the highest-value ones.',
  },
  credit_holds: {
    title: 'Credit Holds',
    icon: ShieldAlert,
    color: 'red',
    toolName: 'check_credit_holds',
    toolParams: {},
    extract: (data) => {
      const r = data?.result || data
      const onHold = r?.count ?? r?.customers_on_hold?.length ?? 0
      return {
        value: String(onHold),
        label: 'On credit hold',
        alert: onHold > 0,
      }
    },
    question: 'Are any customers on credit hold or near their credit limit?',
  },
  sales_backlog: {
    title: 'Sales Backlog',
    icon: ShoppingCart,
    color: 'blue',
    toolName: 'sales_backlog',
    toolParams: {},
    extract: (data) => {
      const r = data?.result || data
      const total = r?.total_backlog ?? r?.total_value ?? r?.summary?.total_backlog_value
      const count = r?.order_count ?? r?.total_orders ?? r?.summary?.total_orders ?? 0
      return {
        value: total != null ? fmtCurrency(total) : String(count),
        label: total != null ? `${count} open orders` : 'Open orders',
      }
    },
    question: "What's our sales backlog? Break it down by customer and month.",
  },
  open_orders: {
    title: 'Open Orders',
    icon: PackageCheck,
    color: 'blue',
    toolName: 'search_orders',
    toolParams: { status: 'Open', limit: 2000, count_only: true },
    extract: (data) => {
      const r = data?.result || data
      const count = r?.total ?? r?.count ?? r?.orders?.length ?? 0
      return { value: String(count), label: 'Open orders' }
    },
    question: 'Show me all open sales orders sorted by date.',
  },
  open_quotes: {
    title: 'Open Quotes',
    icon: FileText,
    color: 'indigo',
    toolName: 'search_quotes',
    toolParams: { status: 'Open', limit: 2000, count_only: true },
    extract: (data) => {
      const r = data?.result || data
      const count = r?.total ?? r?.count ?? r?.quotes?.length ?? 0
      return { value: String(count), label: 'Open quotes' }
    },
    question: 'Show me all open quotes — which need follow-up?',
  },
  recent_shipments: {
    title: 'Due to Ship',
    icon: Truck,
    color: 'teal',
    toolName: 'search_orders',
    toolParams: {
      need_by_from: new Date().toISOString().slice(0, 10),
      need_by_to: new Date(Date.now() + 90 * 86400000).toISOString().slice(0, 10),
      status: 'Open',
      limit: 2000,
      count_only: true,
    },
    extract: (data) => {
      const r = data?.result || data
      const count = r?.total ?? r?.count ?? r?.orders?.length ?? 0
      return {
        value: String(count),
        label: 'Due next 90 days',
      }
    },
    question: "What orders are due to ship in the next 90 days? Show me by customer.",
  },
  po_status: {
    title: 'PO Status',
    icon: ShoppingCart,
    color: 'violet',
    toolName: 'search_pos',
    toolParams: { status: 'Open', limit: 2000, count_only: true },
    extract: (data) => {
      const r = data?.result || data
      const count = r?.total ?? r?.count ?? r?.purchase_orders?.length ?? 0
      return { value: String(count), label: 'Open POs' }
    },
    question: 'Show me open purchase orders — any that need attention?',
  },
  inventory_alerts: {
    title: 'Inventory',
    icon: PackageCheck,
    color: 'cyan',
    toolName: 'search_parts',
    // Count parts flagged run-out (phased out / not reordered) — a real
    // inventory alert and a small, fast subset, instead of scanning the whole
    // ~2k-part master (which ran to the count budget and showed a floor).
    toolParams: { run_out: true, limit: 2000, count_only: true },
    extract: (data) => {
      const r = data?.result || data
      const count = r?.total ?? r?.count ?? 0
      return { value: String(count), label: 'Parts running out', alert: count > 0 }
    },
    question: 'Show me the parts flagged to run out — which are being phased out?',
  },
  quality_metrics: {
    title: 'Quality',
    icon: CheckCircle2,
    color: 'green',
    toolName: 'search_nonconformances',
    toolParams: { status: 'Open', limit: 2000, count_only: true },
    extract: (data) => {
      const r = data?.result || data
      const count = r?.total ?? r?.count ?? r?.nonconformances?.length ?? 0
      return {
        value: String(count),
        label: 'Open NCRs',
        alert: count > 0,
      }
    },
    question: 'Show me open non-conformance reports — what quality issues are outstanding?',
  },
  production_schedule: {
    title: 'Production',
    icon: Calendar,
    color: 'sky',
    toolName: 'get_orders_by_ship_date',
    toolParams: {
      from_date: new Date().toISOString().slice(0, 10),
      to_date: new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10),
    },
    extract: (data) => {
      const r = data?.result || data
      const count = r?.total_releases ?? r?.total ?? r?.releases?.length ?? 0
      return { value: String(count), label: 'Shipping this week' }
    },
    question: "What's shipping this week? Show me the production schedule.",
  },
  margin_summary: {
    title: 'Margin Analysis',
    icon: BarChart3,
    color: 'fuchsia',
    toolName: 'margin_by_job',
    toolParams: {},
    extract: (data) => {
      const r = data?.result || data
      const margins = r?.margins
      if (Array.isArray(margins) && margins.length > 0) {
        return { value: String(margins.length), label: 'Jobs analyzed' }
      }
      const info = r?.costing_info?.domestic_rate
      if (info) return { value: `$${info}/hr`, label: 'Burden rate' }
      return { value: '--', label: 'Margin data' }
    },
    question: 'Show me margin analysis by job — which are most and least profitable?',
  },
}

// Color mappings for Tailwind classes (explicit classes for JIT)
const COLOR_MAP = {
  emerald: { bg: 'bg-emerald-50 dark:bg-emerald-950/30', text: 'text-emerald-600 dark:text-emerald-400', border: 'border-emerald-200 dark:border-emerald-800', badge: 'bg-emerald-100 text-emerald-700' },
  red: { bg: 'bg-red-50 dark:bg-red-950/30', text: 'text-red-600 dark:text-red-400', border: 'border-red-200 dark:border-red-800', badge: 'bg-red-100 text-red-700' },
  orange: { bg: 'bg-orange-50 dark:bg-orange-950/30', text: 'text-orange-600 dark:text-orange-400', border: 'border-orange-200 dark:border-orange-800', badge: 'bg-orange-100 text-orange-700' },
  amber: { bg: 'bg-amber-50 dark:bg-amber-950/30', text: 'text-amber-600 dark:text-amber-400', border: 'border-amber-200 dark:border-amber-800', badge: 'bg-amber-100 text-amber-700' },
  blue: { bg: 'bg-blue-50 dark:bg-blue-950/30', text: 'text-blue-600 dark:text-blue-400', border: 'border-blue-200 dark:border-blue-800', badge: 'bg-blue-100 text-blue-700' },
  indigo: { bg: 'bg-indigo-50 dark:bg-indigo-950/30', text: 'text-indigo-600 dark:text-indigo-400', border: 'border-indigo-200 dark:border-indigo-800', badge: 'bg-indigo-100 text-indigo-700' },
  teal: { bg: 'bg-teal-50 dark:bg-teal-950/30', text: 'text-teal-600 dark:text-teal-400', border: 'border-teal-200 dark:border-teal-800', badge: 'bg-teal-100 text-teal-700' },
  violet: { bg: 'bg-violet-50 dark:bg-violet-950/30', text: 'text-violet-600 dark:text-violet-400', border: 'border-violet-200 dark:border-violet-800', badge: 'bg-violet-100 text-violet-700' },
  cyan: { bg: 'bg-cyan-50 dark:bg-cyan-950/30', text: 'text-cyan-600 dark:text-cyan-400', border: 'border-cyan-200 dark:border-cyan-800', badge: 'bg-cyan-100 text-cyan-700' },
  green: { bg: 'bg-green-50 dark:bg-green-950/30', text: 'text-green-600 dark:text-green-400', border: 'border-green-200 dark:border-green-800', badge: 'bg-green-100 text-green-700' },
  sky: { bg: 'bg-sky-50 dark:bg-sky-950/30', text: 'text-sky-600 dark:text-sky-400', border: 'border-sky-200 dark:border-sky-800', badge: 'bg-sky-100 text-sky-700' },
  fuchsia: { bg: 'bg-fuchsia-50 dark:bg-fuchsia-950/30', text: 'text-fuchsia-600 dark:text-fuchsia-400', border: 'border-fuchsia-200 dark:border-fuchsia-800', badge: 'bg-fuchsia-100 text-fuchsia-700' },
}


// =============================================================================
// Helpers
// =============================================================================

function fmtCurrency(value) {
  if (value == null) return '--'
  const num = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(num)) return '--'
  if (Math.abs(num) >= 1_000_000) return `$${(num / 1_000_000).toFixed(1)}M`
  if (Math.abs(num) >= 1_000) return `$${(num / 1_000).toFixed(0)}K`
  return `$${num.toFixed(0)}`
}

function getGreeting() {
  const hour = new Date().getHours()
  if (hour < 12) return 'Good morning'
  if (hour < 17) return 'Good afternoon'
  return 'Good evening'
}

function formatDate() {
  return new Date().toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
  })
}


// =============================================================================
// WidgetCard — Individual widget with auto-fetch + staggered entrance
// =============================================================================

function WidgetSkeleton({ index = 0, label, icon: Icon, color }) {
  const colors = COLOR_MAP[color] || COLOR_MAP.blue
  return (
    <div
      className="bg-white dark:bg-surface-dark-raised border border-gray-200 dark:border-gray-700 rounded-xl p-4 animate-pulse"
      style={{ animationDelay: `${index * 60}ms` }}
    >
      <div className="flex items-center gap-3 mb-3">
        {Icon ? (
          <div className={`w-9 h-9 ${colors.iconBg} rounded-lg flex items-center justify-center`}>
            <Icon size={18} className={colors.iconColor} />
          </div>
        ) : (
          <div className="w-9 h-9 bg-gray-200 dark:bg-gray-700 rounded-lg" />
        )}
        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
          {label || 'Loading...'}
        </span>
      </div>
      <div className="h-7 bg-gray-200 dark:bg-gray-700 rounded w-16 mb-1" />
      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-20" />
    </div>
  )
}

function WidgetCard({ widgetId, onAskTrendy, index = 0 }) {
  const [state, setState] = useState({ status: 'loading', data: null, error: null })

  const config = WIDGET_REGISTRY[widgetId]

  const fetchData = useCallback(async () => {
    if (!config) return
    setState({ status: 'loading', data: null, error: null })
    try {
      const result = await executeTool(config.toolName, config.toolParams)
      const extracted = config.extract(result)
      setState({ status: 'success', data: extracted, error: null })
    } catch (err) {
      setState({ status: 'error', data: null, error: err.message || 'Failed to load' })
    }
  }, [config])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  if (!config) return null

  const Icon = config.icon
  const colors = COLOR_MAP[config.color] || COLOR_MAP.blue

  if (state.status === 'loading') return <WidgetSkeleton index={index} label={config.label} icon={config.icon} color={config.color} />

  // --- Error state — visually distinct with dashed border ---
  if (state.status === 'error') {
    return (
      <div
        className="bg-gray-50 dark:bg-surface-dark-raised border border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-4 animate-fade-in-up"
        style={{ animationDelay: `${index * 60}ms`, animationFillMode: 'both' }}
      >
        <div className="flex items-center gap-3 mb-2">
          <div className={`w-9 h-9 ${colors.bg} rounded-lg flex items-center justify-center opacity-50`}>
            <Icon size={18} className={colors.text} />
          </div>
          <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{config.title}</span>
        </div>
        <div className="flex items-center gap-2">
          <XCircle size={14} className="text-gray-400" />
          <span className="text-xs text-gray-400">Couldn't load data</span>
          <button
            onClick={fetchData}
            className="ml-auto p-1.5 hover:bg-gray-200 dark:hover:bg-surface-dark-overlay rounded-lg transition-colors"
            title="Retry"
          >
            <RefreshCw size={14} className="text-gray-500" />
          </button>
        </div>
      </div>
    )
  }

  const { value, label, alert } = state.data || {}

  return (
    <button
      onClick={() => onAskTrendy(config.question)}
      className="bg-white dark:bg-surface-dark-raised border border-gray-200 dark:border-gray-700 rounded-xl p-4 text-left hover:border-amtrend-blue/40 hover:shadow-elevation-2 transition-all group w-full animate-fade-in-up"
      style={{ animationDelay: `${index * 60}ms`, animationFillMode: 'both' }}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 ${colors.bg} rounded-lg flex items-center justify-center`}>
            <Icon size={18} className={colors.text} />
          </div>
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{config.title}</span>
        </div>
        <ArrowRight size={14} className="text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
      <div className="pl-12">
        <span className={`text-2xl font-display font-bold ${alert ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-gray-100'}`}>
          {value}
        </span>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{label}</p>
      </div>
    </button>
  )
}


// =============================================================================
// Quick Actions — pre-built prompts below the greeting
// Default set used for admin/exec roles; team users get department-specific
// actions from the /me API response.
// =============================================================================

const DEFAULT_QUICK_ACTIONS = [
  { label: 'Take a Tour', question: "I'd like a tour of Trendy. Walk me through what you can do, how to use you effectively, the Playbooks and Modules tabs, and any tips for getting the most out of this system. Make it a thorough but conversational walkthrough.", icon: '🎓', highlight: true },
  { label: 'Morning briefing', question: 'Give me my morning briefing — alerts, late jobs, cash position, and anything urgent.', icon: '☀️' },
  { label: 'Check my email', question: 'Check my recent emails and summarize anything important.', icon: '📧' },
  { label: "What's shipping?", question: "What's shipping this week? Show me the schedule.", icon: '📦' },
  { label: 'Look up a customer', question: 'I need to look up a customer.', icon: '🔍' },
]

// Icon key → emoji mapping for department quick actions from backend
const ICON_MAP = {
  sun: '☀️', mail: '📧', search: '🔍', truck: '🚚', clock: '⏰',
  'file-text': '📄', 'bar-chart': '📊', 'dollar-sign': '💲', package: '📦',
  layers: '📐', 'git-branch': '🔀', shield: '🛡️', building: '🏢',
  'alert-triangle': '⚠️',
}

/**
 * Build quick actions list: "Take a Tour" + department-specific from API,
 * falling back to DEFAULT_QUICK_ACTIONS if none provided.
 */
function getQuickActions(apiActions) {
  const tourAction = DEFAULT_QUICK_ACTIONS[0] // Always include Take a Tour
  if (!apiActions || apiActions.length === 0) return DEFAULT_QUICK_ACTIONS

  const deptActions = apiActions.map(a => ({
    label: a.label,
    question: a.question,
    icon: ICON_MAP[a.icon] || '💡',
  }))

  return [tourAction, ...deptActions]
}


// Module chip CTAs — explore specific Epicor modules
// Full registry; department config from API specifies which labels to show + order.
const ALL_MODULE_CHIPS = {
  'Customers':       { color: '#8B5CF6', question: 'Show me Customer agents — credit status, AR aging, contacts, and order history.' },
  'Orders':          { color: '#3B82F6', question: 'Show me Order agents — search orders, check releases, ship dates, and balances.' },
  'Quoting':         { color: '#10B981', question: 'Show me Quoting agents — create quotes, benchmark pricing, and check expiring quotes.' },
  'Jobs':            { color: '#F97316', question: 'Show me Job agents — schedules, costs, operations, materials, and labor detail.' },
  'AR & Invoicing':  { color: '#22C55E', question: 'Show me AR agents — invoices, aging, collections alerts, and cash receipts.' },
  'AP & Payments':   { color: '#EF4444', question: 'Show me AP agents — vendor invoices, aging, payment planning, and alerts.' },
  'Parts':           { color: '#06B6D4', question: 'Show me Parts agents — inventory levels, costs, where-used, and transactions.' },
  'Purchasing':      { color: '#6366F1', question: 'Show me Purchasing agents — POs, releases, receipts, and vendor management.' },
  'Shipping':        { color: '#14B8A6', question: 'Show me Shipping agents — shipments, tracking, and uninvoiced shipments.' },
  'Analytics':       { color: '#D946EF', question: 'Run analytics — margin analysis, customer profitability, capacity, and backlog.' },
}

const DEFAULT_MODULE_CHIP_ORDER = Object.keys(ALL_MODULE_CHIPS)

/**
 * Build module chips list ordered by department preference.
 * If apiChipLabels provided, show those first in order, then remaining.
 */
function getModuleChips(apiChipLabels) {
  if (!apiChipLabels || apiChipLabels.length === 0) {
    return DEFAULT_MODULE_CHIP_ORDER.map(label => ({ label, ...ALL_MODULE_CHIPS[label] }))
  }
  // Department-prioritized chips first, then the rest
  const seen = new Set(apiChipLabels)
  const prioritized = apiChipLabels
    .filter(label => ALL_MODULE_CHIPS[label])
    .map(label => ({ label, ...ALL_MODULE_CHIPS[label] }))
  const rest = DEFAULT_MODULE_CHIP_ORDER
    .filter(label => !seen.has(label))
    .map(label => ({ label, ...ALL_MODULE_CHIPS[label] }))
  return [...prioritized, ...rest]
}


// =============================================================================
// LandingPage — Main export
// =============================================================================

export default function LandingPage({ user, onStartChat }) {
  const navigate = useNavigate()
  const widgetIds = user?.landing_widgets || []
  const roleLabel = user?.role_label || 'User'
  const department = user?.department || ''
  const username = user?.username || ''
  const firstName = username.split('.')[0] || username

  // Department-specific content from API (team users) or defaults (admin/exec)
  const quickActions = getQuickActions(user?.landing_quick_actions)
  const moduleChips = getModuleChips(user?.landing_module_chips)

  // Company Brain CTAs — links into the persistent knowledge layer.
  // "Browse" navigates straight to the KB page; the other two route the
  // user back to chat with a primed prompt so Trendy is the front door.
  const brainCtas = [
    {
      label: 'Browse Knowledge',
      sub: 'Wiki of everything Trendy knows',
      icon: Building2,
      gradient: 'from-emerald-500/10 to-emerald-500/0',
      iconBg: 'bg-emerald-100 dark:bg-emerald-900/30',
      iconText: 'text-emerald-600 dark:text-emerald-400',
      onClick: () => navigate('/knowledge'),
    },
    {
      label: 'Ask about anyone',
      sub: 'Pull a 360° briefing on a customer, vendor, or part',
      icon: Search,
      gradient: 'from-violet-500/10 to-violet-500/0',
      iconBg: 'bg-violet-100 dark:bg-violet-900/30',
      iconText: 'text-violet-600 dark:text-violet-400',
      onClick: () => onStartChat('Show me everything we know about '),
    },
    {
      label: 'Teach Trendy',
      sub: 'Capture a rule, a customer note, or a new insight',
      icon: Lightbulb,
      gradient: 'from-amber-500/10 to-amber-500/0',
      iconBg: 'bg-amber-100 dark:bg-amber-900/30',
      iconText: 'text-amber-600 dark:text-amber-400',
      onClick: () => onStartChat('I want to teach you something. Help me capture it as a knowledge entry: '),
    },
  ]

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-4xl mx-auto px-3 sm:px-6 py-6 sm:py-10">
        {/* Greeting */}
        <div className="mb-6 sm:mb-8 animate-fade-in-up" style={{ animationFillMode: 'both' }}>
          <div className="flex items-center gap-3 mb-2">
            <img
              src="/amtrend-logo.png"
              alt="Amtrend"
              className="h-8 sm:h-10"
            />
            <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-amtrend-blue/10 text-amtrend-blue">
              {roleLabel}
            </span>
            {department && department !== roleLabel && (
              <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
                {department}
              </span>
            )}
          </div>
          <h1 className="text-2xl sm:text-3xl font-display font-bold tracking-tight text-gray-900 dark:text-gray-100 mt-3">
            {getGreeting()}, {firstName}
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">{formatDate()}</p>
        </div>

        {/* Quick Actions — department-specific */}
        <div className="flex flex-wrap gap-2 mb-6 sm:mb-8 animate-fade-in-up"
          style={{ animationDelay: '80ms', animationFillMode: 'both' }}>
          {quickActions.map((action) => (
            <button
              key={action.label}
              onClick={() => onStartChat(action.question)}
              className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm transition-all ${
                action.highlight
                  ? 'bg-amtrend-blue/10 dark:bg-amtrend-blue/20 border border-amtrend-blue/30 text-amtrend-blue dark:text-blue-300 hover:bg-amtrend-blue/20 dark:hover:bg-amtrend-blue/30 font-medium'
                  : 'bg-white dark:bg-surface-dark-raised border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-amtrend-blue/40 hover:bg-gray-50 dark:hover:bg-surface-dark-overlay'
              }`}
            >
              <span>{action.icon}</span>
              <span>{action.label}</span>
            </button>
          ))}
        </div>

        {/* Your Company Brain — entry points to the persistent knowledge
            layer. Sits between live chat actions (Quick Actions) and live
            ERP modules so it visually anchors KB as a first-class peer. */}
        <div className="mb-6 sm:mb-8 animate-fade-in-up"
          style={{ animationDelay: '120ms', animationFillMode: 'both' }}>
          <div className="flex items-center gap-2 mb-2.5">
            <Brain size={14} className="text-amtrend-blue" />
            <div className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Your Company Brain
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {brainCtas.map((c, i) => {
              const Icon = c.icon
              return (
                <button
                  key={c.label}
                  onClick={c.onClick}
                  className={`relative overflow-hidden text-left bg-gradient-to-br ${c.gradient} bg-white dark:bg-surface-dark-raised border border-gray-200 dark:border-gray-700 rounded-xl p-3.5 hover:border-amtrend-blue/40 hover:shadow-elevation-2 transition-all group`}
                >
                  <div className="flex items-start gap-2.5">
                    <div className={`w-9 h-9 ${c.iconBg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                      <Icon size={18} className={c.iconText} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          {c.label}
                        </span>
                        <ArrowRight size={14} className="text-gray-300 dark:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                      </div>
                      <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 leading-snug">
                        {c.sub}
                      </p>
                    </div>
                  </div>
                </button>
              )
            })}
          </div>
        </div>

        {/* Module Chips — department-prioritized order */}
        <div className="mb-6 sm:mb-8 animate-fade-in-up"
          style={{ animationDelay: '160ms', animationFillMode: 'both' }}>
          <div className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2.5">
            Explore Modules
          </div>
          <div className="flex flex-wrap gap-1.5">
            {moduleChips.map((chip) => (
              <button
                key={chip.label}
                onClick={() => onStartChat(chip.question)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-white dark:bg-surface-dark-raised border border-gray-200 dark:border-gray-700 rounded-lg text-[12px] font-medium text-gray-600 dark:text-gray-300 hover:border-amtrend-blue/40 hover:bg-gray-50 dark:hover:bg-surface-dark-overlay transition-all"
              >
                <span
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ background: chip.color }}
                />
                {chip.label}
              </button>
            ))}
          </div>
        </div>

        {/* Widget Grid — staggered entrance via per-card animation-delay */}
        {widgetIds.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {widgetIds.map((id, i) => (
              <WidgetCard
                key={id}
                widgetId={id}
                onAskTrendy={onStartChat}
                index={i}
              />
            ))}
          </div>
        )}

        {/* Fallback when no widgets */}
        {widgetIds.length === 0 && (
          <div className="text-center py-8 animate-fade-in">
            <p className="text-gray-400 dark:text-gray-500 text-sm">
              Ask Trendy anything about orders, quotes, jobs, customers, or finances.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
